import { Component, OnInit, ViewChild, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { RosterService } from '../../services/roster/roster.service';
import { SystemSettingsService } from '../system/settings/system-settings.service';
import * as _moment from 'moment';
const moment = (_moment as any).default || _moment;

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Optional } from '@angular/core';
import { ShiftDialogComponent } from './shift-dialog/shift-dialog.component';
import { DeleteShiftDialogComponent } from './shift-dialog/delete-shift-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { RosterListComponent } from './roster-list/roster-list.component';
import { environment } from '../../../environments/environment';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { SnackbarService } from '../../common/services/snackbar.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../common/components/confirm-dialog/confirm-dialog.component';
import { RosterCloneDialogComponent } from './roster-clone-dialog/roster-clone-dialog.component';
import { AppConstants } from 'src/app/common/app.constants';
import { PermissionService } from '../../auth/permission.service';

interface Task {
  id: number;
  code: string;
  name: string;
  fg_color: string;
  bg_color: string;
}

export interface Shift {
  id?: number;
  employee_id: number;
  shift_date: string;
  shift_name?: string;
  start_time: string;
  end_time: string;
  task_id1?: number;
  task_id2?: number;
  task_id3?: number;
  task1_code?: string;
  task2_code?: string;
  task3_code?: string;
  is_in_charge: boolean;
  notes?: string;
  is_readonly?: boolean;
  shop_id?: number;
  shop_code?: string;
  roster_shop_id?: number;
  is_unscheduled?: boolean;
  attendance_id?: number;
  act_shift_start_time?: string;
  act_shift_end_time?: string;
  upb?: string;
  entry_reason?: number;
  entry_reason_others?: string;
  pos_id?: number;
  is_finalized?: boolean;
  is_deleted?: boolean;
  is_shift_deleted?: boolean;
  is_attendance_deleted?: boolean;
  is_temp_deleted?: boolean;
  is_absent?: boolean;
  is_dirty?: boolean;
  employee_type_id?: number;
  job_position_id?: number;
  job_title_id?: number;
}

interface Employee {
  id: number;
  f_name: string;
  l_name: string;
  preferred_name: string;
  display_name: string;
  name?: string;
  code?: string;
  position_name: string;
  title_name?: string;
  employee_type_name: string;
  min_work_hours: number;
  max_work_hours: number;
  cost_per_hour: number;
  is_partner: boolean;
  hierarchy_order: number;
  grade: number;
  fg_color?: string;
  position_bg_color?: string;
  type_bg_color?: string;
  employee_type_id?: number;
  job_position_id?: number;
  job_title_id?: number;
  primary_shop_code?: string;
  receive_roster_by_mail?: number;
}

import { ShopSelectDialogComponent } from './shop-select-dialog/shop-select-dialog.component';
import { SelectWeekDialogComponent } from './select-week-dialog/select-week-dialog.component';
import { RosterExportDialogComponent } from './roster-export-dialog/roster-export-dialog.component';
import { DirtyCheck } from '../../common/guards/form-deactivate.guard';

@Component({
  selector: 'app-roster',
  templateUrl: './roster.component.html',
  styleUrls: ['./roster.component.scss', './roster.component.print.scss'],
  standalone: false
})
export class RosterComponent implements OnInit, OnDestroy, DirtyCheck {
  @ViewChild('rosterContainer') rosterContainer!: ElementRef;
  @ViewChild('rosterViewport') rosterViewport!: ElementRef;

  private headerMetricSubject = new Subject<void>();

  hasUnsavedChanges = false;
  isLoading = false;
  isSaving = false;

  currentDate = moment().startOf('isoWeek'); // Monday
  weekDays: any[] = [];
  employees: Employee[] = [];
  shifts: Shift[] = [];
  tasks: Task[] = [];
  masterShifts: any[] = [];
  reasons: any[] = [];
  header: any = null;
  weekDefs: any[] = [];
  currentWeekNo: number = 0;
  currentYear: number = 0;
  jobBreaks: any[] = [];
  shopEmployees: any[] = [];
  actualSales: any = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
  settingsColors: any = {
    unavilable_bg_color: null,
    leave_bg_color: null,
    inc_color: null,
    rostered_hour_color: null,
    actual_hour_color: null,
    att_threshold_in_before: null,
    att_threshold_in_after: null,
    att_threshold_out_before: null,
    att_threshold_out_after: null
  };

  searchQuery: string = '';
  showSearchInput: boolean = false;
  hideEmptyEmployees: boolean = false;
  sortOrder: 'asc' | 'desc' | null = null;
  showEmployeeMetrics: boolean = false;
  showEmployeeDetails: boolean = false;
  showRounded: boolean = false;
  isFullscreen: boolean = false;
  rosterSettings: any = {
    no_of_shifts: 0
  };
  userShops: any[] = [];
  selectedShopIds: number[] = [];
  currentUser: any = null;
  employeeColumnWidth: number = 240;
  shiftColumnWidth: number = 156;
  summaryColumnWidth: number = 110;
  IsDownloading: boolean = false;
  lastEditedShiftId: number | null = null;

  isNoShopsSelected(): boolean {
    return (this.currentUser?.user_type === 'HQ' || this.currentUser?.user_type === 'AREA_MANAGER') &&
      (!this.selectedShopIds || this.selectedShopIds.length === 0);
  }

  get filteredAndSortedEmployees() {
    let result = [...this.employees];

    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      result = result.filter(e =>
        (e.preferred_name && e.preferred_name.toLowerCase().includes(q)) ||
        (e.display_name && e.display_name.toLowerCase().includes(q)) ||
        (e.f_name && e.f_name.toLowerCase().includes(q)) ||
        (e.l_name && e.l_name.toLowerCase().includes(q)) ||
        (e.code && e.code.toLowerCase().includes(q)) ||
        (e.title_name && e.title_name.toLowerCase().includes(q))
      );
    }

    if (this.hideEmptyEmployees) {
      if (this.isPastWeek()) {
        result = result.filter(e => this.getEmployeeFilteredHours(e.id) > 0);
      } else {
        result = result.filter(e => {
          const isUnavailableAllWeek = this.weekDays.every(day =>
            !this.isEmployeeActiveOnDate(e.id, day.date) || this.isEmployeeOnLeave(e.id, day.date)
          );
          return !isUnavailableAllWeek;
        });
      }
    }

    if (this.sortOrder === 'asc') {
      result.sort((a, b) => this.getEmployeeDisplayName(a).localeCompare(this.getEmployeeDisplayName(b)));
    } else if (this.sortOrder === 'desc') {
      result.sort((a, b) => this.getEmployeeDisplayName(b).localeCompare(this.getEmployeeDisplayName(a)));
    }

    return result;
  }

  toggleSort() {
    if (!this.sortOrder) this.sortOrder = 'asc';
    else if (this.sortOrder === 'asc') this.sortOrder = 'desc';
    else this.sortOrder = null;
  }

  getEmployeeDisplayName(emp: any): string {
    if (!emp) return '';
    const fName = emp.f_name || '';
    const lName = emp.l_name || '';
    const fullName = (fName + ' ' + lName).trim();
    if (emp.preferred_name && emp.preferred_name.trim()) {
      return `${emp.preferred_name.trim()} - ${fullName}`;
    }
    return fullName;
  }

  toggleSearch() {
    this.showSearchInput = !this.showSearchInput;
    if (!this.showSearchInput) this.searchQuery = '';
  }

  toggleEmployeeDetails() {
    this.showEmployeeDetails = !this.showEmployeeDetails;
  }

  toggleMetrics() {
    this.showEmployeeMetrics = !this.showEmployeeMetrics;
  }

  toggleRounded() {
    this.showRounded = !this.showRounded;
  }

  confirmIfDirty(action: () => void, message: string) {
    if (this.isDirty()) {
      const confirmData = new ConfirmDialogModel('Unsaved Changes', message);
      const confirmRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: confirmData
      });
      confirmRef.afterClosed().subscribe(res => {
        if (res) {
          action();
        }
      });
    } else {
      action();
    }
  }

  openShopSelection() {
    this.confirmIfDirty(() => {
      const dialogRef = this.dialog.open(ShopSelectDialogComponent, {
        width: '500px',
        data: {
          shops: this.userShops,
          selectedIds: this.selectedShopIds
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.selectedShopIds = result;
          this.loadRoster();
        }
      });
    }, 'You have unsaved changes in this roster. If you change the shop selection, your unsaved changes will be lost. Do you want to proceed?');
  }

  preFullscreenAutoFit: boolean = false;

  @HostListener('document:fullscreenchange')
  onFullscreenChange() {
    const wasFullscreen = this.isFullscreen;
    this.isFullscreen = !!document.fullscreenElement;

    if (this.isFullscreen && !wasFullscreen) {
      this.preFullscreenAutoFit = this.isAutoFit;
      this.isAutoFit = true;
    } else if (!this.isFullscreen && wasFullscreen) {
      this.isAutoFit = this.preFullscreenAutoFit;
    }

    this.updateZoom();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateZoom();
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      const elem = this.rosterContainer.nativeElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err: any) => {
          console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          this.isFullscreen = true; // Fallback to CSS
          this.preFullscreenAutoFit = this.isAutoFit;
          this.isAutoFit = true;
          this.updateZoom();
        });
      } else {
        this.isFullscreen = true; // Fallback to CSS
        this.preFullscreenAutoFit = this.isAutoFit;
        this.isAutoFit = true;
        this.updateZoom();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  constructor(
    private rosterService: RosterService,
    private dialog: MatDialog,
    private http: HttpClient,
    private snackBarService: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private settingsService: SystemSettingsService,
    private permissionService: PermissionService
  ) { }

  get canViewAttendance(): boolean {
    return this.permissionService.getDataPermission('attendance').can_view === 1;
  }

  get canEditAttendance(): boolean {
    return this.permissionService.getDataPermission('attendance').can_edit === 1;
  }

  get canViewShift(): boolean {
    return true; // Shift can_view is always true
  }

  get canEditShift(): boolean {
    return this.permissionService.getDataPermission('shift').can_edit === 1;
  }

  get canViewWagesCost(): boolean {
    return this.permissionService.getDataPermission('wages-cost').can_view === 1;
  }

  get canEditWagesCost(): boolean {
    return this.permissionService.getDataPermission('wages-cost').can_edit === 1;
  }

  get canViewSales(): boolean {
    return this.permissionService.getDataPermission('sales').can_view === 1;
  }

  get canEditSales(): boolean {
    return this.permissionService.getDataPermission('sales').can_edit === 1;
  }

  get canShowWagesRow(): boolean {
    return this.canViewWagesCost;
  }

  get canShowSalesRow(): boolean {
    return this.canViewSales;
  }

  get canShowTargetVarianceRows(): boolean {
    return this.canViewWagesCost && this.canViewSales;
  }

  isViewerMode(): boolean {
    return this.router.url.includes('/roster/viewer');
  }

  showEmployeeColumn: boolean = true;
  showSummaryColumn: boolean = true;
  zoomLevel: number = 100;
  isAutoFit: boolean = false;

  toggleEmployeeColumn() {
    this.showEmployeeColumn = !this.showEmployeeColumn;
    this.updateZoom();
  }

  toggleSummaryColumn() {
    this.showSummaryColumn = !this.showSummaryColumn;
    this.updateZoom();
  }

  setZoom(level: number) {
    this.zoomLevel = level;
    localStorage.setItem('rosterZoomLevel', String(level));
  }

  toggleAutoFit() {
    this.isAutoFit = !this.isAutoFit;
    localStorage.setItem('rosterAutoFit', String(this.isAutoFit));
    this.updateZoom();
  }

  getUnscaledRosterWidth(): number {
    let w = 0;
    if (this.showEmployeeColumn) w += this.employeeColumnWidth;
    w += 7 * this.shiftColumnWidth;
    if (this.showSummaryColumn) w += this.summaryColumnWidth;
    return w + 2; // 2px safety margin for borders
  }

  updateZoom() {
    if (!this.isAutoFit) {
      this.zoomLevel = 100;
      return;
    }

    if (this.rosterViewport && this.rosterViewport.nativeElement) {
      this.calculateAutoFitZoom();

      // Fullscreen and maximizing transitions can take some time, recalculate after 300ms to be sure
      setTimeout(() => {
        this.calculateAutoFitZoom();
      }, 300);
    }
  }

  private calculateAutoFitZoom() {
    if (this.rosterViewport && this.rosterViewport.nativeElement) {
      const viewportWidth = this.rosterViewport.nativeElement.clientWidth;
      const unscaledWidth = this.getUnscaledRosterWidth();
      if (viewportWidth && unscaledWidth) {
        const calculatedScale = (viewportWidth / unscaledWidth) * 100;
        this.zoomLevel = Math.min(200, Math.max(50, calculatedScale));
      }
    }
  }

  getGridTemplateColumns(): string {
    let cols = '';
    if (this.showEmployeeColumn) cols += this.employeeColumnWidth + 'px ';
    cols += 'repeat(7, ' + this.shiftColumnWidth + 'px)';
    if (this.showSummaryColumn) cols += ' ' + this.summaryColumnWidth + 'px';
    return cols;
  }

  ngOnInit(): void {
    const savedAutoFit = localStorage.getItem('rosterAutoFit');
    if (savedAutoFit) {
      this.isAutoFit = savedAutoFit === 'true';
    }
    if (this.isViewerMode()) {
      this.isAutoFit = true;
    }
    if (this.isAutoFit) {
      const savedZoom = localStorage.getItem('rosterZoomLevel');
      if (savedZoom) {
        this.zoomLevel = Number(savedZoom);
      }
    } else {
      this.zoomLevel = 100;
    }

    setTimeout(() => {
      this.updateZoom();
    }, 500);

    // Debounced auto-save for footer metrics (sales forecast and wage target %)
    this.headerMetricSubject.pipe(
      debounceTime(600)
    ).subscribe(() => {
      if (this.hasUnsavedChanges) {
        this.saveRoster(true);
      }
    });

    // Load dynamic roster settings from database
    this.settingsService.getSettings().subscribe((res: any) => {
      if (res.status === 'SUCCESS' && res.data && res.data.settings) {
        this.settingsColors = res.data.settings;
      }
    });

    // Load settings from session
    const userDataStr = sessionStorage.getItem(AppConstants.SESSION_VAR_USER);
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      this.currentUser = userData;
      console.log('User Session Data:', userData);
      if (userData.app_settings && userData.app_settings.roster) {
        this.rosterSettings = { ...this.rosterSettings, ...userData.app_settings.roster };
        console.log('Roster Settings Applied:', this.rosterSettings);
      }
      if (userData.user_shops) {
        this.userShops = userData.user_shops;
        if (userData.user_type === 'HQ' || userData.user_type === 'AREA_MANAGER') {
          this.selectedShopIds = [];
        } else {
          const loggedInShopId = Number(userData.shop_id);
          const hasLoggedInShop = this.userShops.some(s => Number(s.id) === loggedInShopId);
          if (loggedInShopId && hasLoggedInShop) {
            this.selectedShopIds = [loggedInShopId];
          } else if (this.userShops.length > 0) {
            this.selectedShopIds = [Number(this.userShops[0].id)];
          } else {
            this.selectedShopIds = [];
          }
        }
      }
    }

    this.rosterService.getWeekDefs().subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.weekDefs = res.data || [];
      }
      this.initRouteParams();
    }, () => {
      this.initRouteParams();
    });
  }

  initRouteParams() {
    this.route.queryParams.subscribe(params => {
      let shopIdChanged = false;
      if (params['shop_id']) {
        const urlShopId = Number(params['shop_id']);
        if (!this.selectedShopIds.includes(urlShopId) || this.selectedShopIds.length !== 1) {
          this.selectedShopIds = [urlShopId];
          shopIdChanged = true;
        }
      }

      if (params['week_number'] && params['year']) {
        const week = Number(params['week_number']);
        const year = Number(params['year']);
        if (this.currentWeekNo === week && this.currentYear === year && !shopIdChanged) {
          return;
        }
      }
      let weekDef;
      if (params['week_number'] && params['year']) {
        const week = Number(params['week_number']);
        const year = Number(params['year']);
        weekDef = this.weekDefs.find(w => Number(w.week_no) === week && Number(w.year) === year);
        if (!weekDef) {
          this.currentWeekNo = week;
          this.currentYear = year;
          this.currentDate = moment().year(year).isoWeek(week).startOf('isoWeek');
        }
      } else {
        const today = moment();
        weekDef = this.weekDefs.find(w => today.isBetween(moment(w.start_date), moment(w.end_date), 'day', '[]'));
        if (!weekDef && this.weekDefs.length > 0) {
          // If today doesn't fall in any defined week, default to the current active week or first available
          weekDef = this.weekDefs.find(w => moment().isSameOrAfter(moment(w.start_date))) || this.weekDefs[0];
        }
      }

      if (weekDef) {
        this.currentWeekNo = Number(weekDef.week_no);
        this.currentYear = Number(weekDef.year);
        this.currentDate = moment(weekDef.start_date);
      } else if (!this.currentWeekNo) {
        this.currentDate = moment().startOf('isoWeek');
        this.currentWeekNo = this.currentDate.isoWeek();
        this.currentYear = this.currentDate.year();
      }

      this.generateWeekDays();
      this.loadRoster();
    });
  }

  generateWeekDays() {
    this.weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = moment(this.currentDate).add(i, 'days');
      this.weekDays.push({
        date: date.format('YYYY-MM-DD'),
        dayName: date.format('ddd'),
        dayNum: date.format('D'),
        monthName: date.format('MMM'),
        fullDisplay: date.format('ddd D MMM')
      });
    }
  }


  onDragMoved(event: any) {
    if (!this.rosterViewport) return;

    const viewport = this.rosterViewport.nativeElement;
    const rect = viewport.getBoundingClientRect();
    const pointerY = event.pointerPosition.y;

    const threshold = 80; // Distance from edge to start scrolling
    const speed = 15;      // Scroll speed

    if (pointerY > rect.bottom - threshold) {
      viewport.scrollTop += speed;
    } else if (pointerY < rect.top + threshold) {
      viewport.scrollTop -= speed;
    }
  }

  scrollToTop() {
    setTimeout(() => {
      if (this.rosterViewport && this.rosterViewport.nativeElement) {
        this.rosterViewport.nativeElement.scrollTop = 0;
        this.rosterViewport.nativeElement.scrollLeft = 0;
      }
    });
  }

  loadRoster() {
    const weekNumber = this.currentWeekNo || this.currentDate.isoWeek();
    const year = this.currentYear || this.currentDate.year();

    if (this.isNoShopsSelected()) {
      this.employees = [];
      this.shifts = [];
      this.tasks = [];
      this.masterShifts = [];
      this.reasons = [];
      this.jobBreaks = [];
      this.shopEmployees = [];
      this.actualSales = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
      this.hasUnsavedChanges = false;
      this.lastEditedShiftId = null;

      let startDate = this.currentDate.format('YYYY-MM-DD');
      let endDate = moment(this.currentDate).add(6, 'days').format('YYYY-MM-DD');

      const weekDef = this.weekDefs.find(w => Number(w.week_no) === weekNumber && Number(w.year) === year);
      if (weekDef) {
        startDate = moment(weekDef.start_date).format('YYYY-MM-DD');
        endDate = moment(weekDef.end_date).format('YYYY-MM-DD');
      }

      this.header = {
        week_number: weekNumber,
        year: year,
        display_year: weekDef?.display_year || year.toString(),
        start_date: startDate,
        end_date: endDate,
        mon_sales_forecast: 0, tue_sales_forecast: 0, wed_sales_forecast: 0, thu_sales_forecast: 0, fri_sales_forecast: 0, sat_sales_forecast: 0, sun_sales_forecast: 0,
        mon_wage_target: 0, tue_wage_target: 0, wed_wage_target: 0, thu_wage_target: 0, fri_wage_target: 0, sat_wage_target: 0, sun_wage_target: 0
      };
      return;
    }

    this.isLoading = true;
    this.rosterService.getRoster(weekNumber, year, this.selectedShopIds).subscribe((res: any) => {
      this.isLoading = false;
      if (res.status === 'SUCCESS') {
        this.employees = res.data.employees;
        this.shifts = res.data.details;
        this.tasks = res.data.tasks;
        this.masterShifts = res.data.masterShifts || [];
        this.reasons = (res.data.reasons || []).sort((a: any, b: any) => {
          const orderA = (!a.display_order || Number(a.display_order) === 0) ? 999 : Number(a.display_order);
          const orderB = (!b.display_order || Number(b.display_order) === 0) ? 999 : Number(b.display_order);
          if (orderA !== orderB) return orderA - orderB;
          return (a.description || '').localeCompare(b.description || '', undefined, { sensitivity: 'base' });
        });
        this.jobBreaks = res.data.jobBreaks || [];
        this.shopEmployees = res.data.shopEmployees || [];
        this.actualSales = res.data.actualSales || { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
        this.hasUnsavedChanges = false;
        this.lastEditedShiftId = null;
        this.scrollToTop();

        let startDate = this.currentDate.format('YYYY-MM-DD');
        let endDate = moment(this.currentDate).add(6, 'days').format('YYYY-MM-DD');

        const weekDef = this.weekDefs.find(w => Number(w.week_no) === weekNumber && Number(w.year) === year);
        if (weekDef) {
          startDate = moment(weekDef.start_date).format('YYYY-MM-DD');
          endDate = moment(weekDef.end_date).format('YYYY-MM-DD');
        }

        this.header = res.data.header || {
          week_number: weekNumber,
          year: year,
          display_year: weekDef?.display_year || year.toString(),
          start_date: startDate,
          end_date: endDate,
          mon_sales_forecast: 0, tue_sales_forecast: 0, wed_sales_forecast: 0, thu_sales_forecast: 0, fri_sales_forecast: 0, sat_sales_forecast: 0, sun_sales_forecast: 0,
          mon_wage_target: 0, tue_wage_target: 0, wed_wage_target: 0, thu_wage_target: 0, fri_wage_target: 0, sat_wage_target: 0, sun_wage_target: 0
        };

        if (this.header && !this.header.display_year && weekDef) {
          this.header.display_year = weekDef.display_year;
        }

        // Automatically default to Actual Attendance (showRounded = false) when loading a Past Roster
        const isPastRoster = this.currentDate.isBefore(moment().startOf('isoWeek'), 'day');
        if (isPastRoster) {
          this.showRounded = false;
        }
      }
    }, () => {
      this.isLoading = false;
    });
  }

  getShiftsForEmployeeAndDate(employeeId: number, date: string) {
    const targetDate = date ? date.slice(0, 10) : '';
    return this.shifts
      .filter(s => Number(s.employee_id) === Number(employeeId) && (s.shift_date ? s.shift_date.slice(0, 10) : '') === targetDate && !s.is_deleted)
      .sort((a, b) => {
        if (!a.start_time && !b.start_time) return 0;
        if (!a.start_time) return 1;
        if (!b.start_time) return -1;
        return a.start_time.localeCompare(b.start_time);
      });
  }

  hasDirtyShift(employeeId: number, date: string): boolean {
    const dayShifts = this.getShiftsForEmployeeAndDate(employeeId, date);
    return dayShifts.some(s => s.is_dirty && !this.isInactiveShift(s));
  }

  hasLastEditedShift(employeeId: number, date: string): boolean {
    const dayShifts = this.getShiftsForEmployeeAndDate(employeeId, date);
    return dayShifts.some(s => s.id === this.lastEditedShiftId && !this.isInactiveShift(s));
  }

  getLeaveForEmployeeAndDate(employeeId: number, date: string) {
    if (!this.jobBreaks || this.jobBreaks.length === 0) return null;
    const checkDate = moment(date, 'YYYY-MM-DD');
    return this.jobBreaks.find(b =>
      Number(b.employee_id) === Number(employeeId) &&
      checkDate.isSameOrAfter(moment(b.start_date, 'YYYY-MM-DD')) &&
      checkDate.isSameOrBefore(moment(b.end_date, 'YYYY-MM-DD'))
    );
  }

  isEmployeeOnLeave(employeeId: number, date: string): boolean {
    return !!this.getLeaveForEmployeeAndDate(employeeId, date);
  }

  isEmployeeActiveOnDate(employeeId: number, date: string): boolean {
    return !!this.getDefaultShopForEmployeeAndDate(employeeId, date);
  }

  canAddShift(employeeId: number, date: string, shopId?: number): boolean {
    if (!this.isEmployeeActiveOnDate(employeeId, date)) return false;
    if (this.isEmployeeOnLeave(employeeId, date)) return false;

    const limit = Number(this.rosterSettings.no_of_shifts || this.rosterSettings.no_of_shift);
    if (!limit || limit <= 0) return true;

    // Filter shifts to only count those in the specified shop(s)
    const targetShopIds = shopId ? [Number(shopId)] : this.selectedShopIds.map(Number);
    const existingShifts = this.getShiftsForEmployeeAndDate(employeeId, date).filter(s => {
      const shiftShopId = Number(s.shop_id || s.roster_shop_id || 0);
      return targetShopIds.includes(shiftShopId);
    });

    return existingShifts.length < limit;
  }

  getShiftDuration(shift: any) {
    const start = moment(shift.start_time, 'HH:mm');
    const end = moment(shift.end_time, 'HH:mm');
    let durationMinutes = end.diff(start, 'minutes');
    if (durationMinutes < 0) durationMinutes += 24 * 60;
    const hours = durationMinutes / 60;
    return hours.toFixed(2) + 'h';
  }

  getShiftDurationFormatted(shift: any) {
    if (!shift.start_time || shift.start_time === '' || !shift.end_time || shift.end_time === '') {
      return '--:--';
    }
    const start = moment(shift.start_time, 'HH:mm');
    const end = moment(shift.end_time, 'HH:mm');
    let durationMinutes = end.diff(start, 'minutes');
    if (durationMinutes < 0) durationMinutes += 24 * 60;
    if (durationMinutes === 0) {
      return '--:--';
    }
    const hours = durationMinutes / 60;
    return hours.toFixed(2) + 'h';
  }

  onClose() {
    this.router.navigate(['/roster']);
  }

  onCloneRoster() {
    if (!this.header) {
      this.snackBarService.open('Save the roster before cloning', 'info');
      return;
    }

    const dialogRef = this.dialog.open(RosterCloneDialogComponent, {
      width: '450px',
      data: { source_week: this.header.week_number, source_year: this.header.year }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const cloneParams = {
          source_week: this.header.week_number,
          source_year: this.header.year,
          target_week: result.target_week,
          target_year: result.target_year,
          clone_type: result.clone_type
        };

        this.rosterService.cloneRoster({ ...cloneParams, check_only: true }).subscribe((res: any) => {
          if (res.status === 'EXISTS') {
            const dialogData = new ConfirmDialogModel(
              'Overwrite Roster',
              res.message || `Roster for target week already exists. Do you want to overwrite it?`
            );
            const confirmRef = this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: dialogData
            });

            confirmRef.afterClosed().subscribe(confirmResult => {
              if (confirmResult) {
                this.executeRosterClone(cloneParams);
              }
            });
          } else {
            this.executeRosterClone(cloneParams);
          }
        });
      }
    });
  }

  private executeRosterClone(cloneParams: any) {
    this.rosterService.cloneRoster(cloneParams).subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.snackBarService.open(res.message || 'Roster successfully cloned', 'success');
        this.router.navigate(['/roster/planner'], { queryParams: { week_number: cloneParams.target_week, year: cloneParams.target_year } });
      } else {
        this.snackBarService.open(res.message, 'error');
      }
    });
  }

  deleteShift(id: number) {
    const shiftToDelete = this.shifts.find(s => s.id === id);
    if (!shiftToDelete) return;

    const dialogRef = this.dialog.open(DeleteShiftDialogComponent, {
      width: '400px',
      data: { shift: shiftToDelete }
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        if (dialogResult.deleteAttendance) {
          // Deleting attendance should delete both attendance and shift
          shiftToDelete.is_deleted = true;
          shiftToDelete.is_shift_deleted = true;
          shiftToDelete.is_attendance_deleted = true;
          shiftToDelete.is_unscheduled = true;
          shiftToDelete.start_time = '';
          shiftToDelete.end_time = '';
          shiftToDelete.act_shift_start_time = '';
          shiftToDelete.act_shift_end_time = '';
        } else if (dialogResult.deleteShift) {
          // Deleting shift ONLY deletes shift
          shiftToDelete.is_shift_deleted = true;
          shiftToDelete.is_unscheduled = true;
          shiftToDelete.start_time = '';
          shiftToDelete.end_time = '';
        }
        shiftToDelete.is_dirty = true;
        this.lastEditedShiftId = shiftToDelete.id ?? null;
        this.hasUnsavedChanges = true;
      }
    });
  }

  finalizeAttendance(shift: any) {
    if (!shift) return;

    if (!shift.attendance_id) {
      // If attendance_id is not yet in local state, save first to obtain attendance_id from server
      this.saveRoster(true, () => {
        const match = this.shifts.find(s => s.id == shift.id);
        if (match && match.attendance_id) {
          this.finalizeAttendance(match);
        } else {
          this.snackBarService.open('Cannot finalize: No attendance record found for this shift.', 'error');
        }
      });
      return;
    }

    const dialogData = new ConfirmDialogModel('Finalize Attendance', 'Are you sure you want to finalize this attendance?');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const payload = {
          attendance_id: shift.attendance_id,
          shop_id: shift.shop_id || this.selectedShopIds[0]
        };

        this.rosterService.finalizeAttendance(payload).subscribe({
          next: (response) => {
            if (response.status === 'SUCCESS') {
              shift.is_finalized = true;
              shift.is_readonly = true;
              this.snackBarService.open('Attendance finalized successfully.', 'success');
            } else {
              this.snackBarService.open(response.message || 'Failed to finalize attendance.', 'error');
            }
          },
          error: (err) => {
            console.error('Error finalizing attendance', err);
            this.snackBarService.open('Error finalizing attendance.', 'error');
          }
        });
      }
    });
  }

  undoFinalizeAttendance(shift: any) {
    if (!shift) return;

    if (!shift.attendance_id) {
      this.saveRoster(true, () => {
        const match = this.shifts.find(s => s.id == shift.id);
        if (match && match.attendance_id) {
          this.undoFinalizeAttendance(match);
        } else {
          this.snackBarService.open('Cannot undo finalize: No attendance record found for this shift.', 'error');
        }
      });
      return;
    }

    const dialogData = new ConfirmDialogModel('Undo Finalize', 'Are you sure you want to undo the finalize for this attendance?');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const payload = {
          attendance_id: shift.attendance_id,
          shop_id: shift.shop_id || this.selectedShopIds[0]
        };

        this.rosterService.undoFinalizeAttendance(payload).subscribe({
          next: (response) => {
            if (response.status === 'SUCCESS') {
              shift.is_finalized = false;
              shift.is_readonly = false;
              this.snackBarService.open('Undo finalize successful.', 'success');
            } else {
              this.snackBarService.open(response.message || 'Failed to undo finalize attendance.', 'error');
            }
          },
          error: (err) => {
            console.error('Error undoing finalize attendance', err);
            this.snackBarService.open('Error undoing finalize attendance.', 'error');
          }
        });
      }
    });
  }

  isInactiveShift(shift: any): boolean {
    return !this.selectedShopIds.map(Number).includes(Number(shift.shop_id || shift.roster_shop_id || 0));
  }

  isOtherShopShift(shift: any): boolean {
    return !this.selectedShopIds.map(Number).includes(Number(shift.shop_id || shift.roster_shop_id || 0));
  }

  hasOverlap(newShift: Shift): boolean {
    const newDate = newShift.shift_date ? newShift.shift_date.slice(0, 10) : '';
    return this.shifts.some(s => {
      const sDate = s.shift_date ? s.shift_date.slice(0, 10) : '';
      if (s.is_deleted || s.id === newShift.id || Number(s.employee_id) !== Number(newShift.employee_id) || sDate !== newDate) {
        return false;
      }
      if (!newShift.start_time || !newShift.end_time || !s.start_time || !s.end_time) {
        return false;
      }

      const nStart = moment(newShift.start_time.slice(0, 5), 'HH:mm');
      const nEnd = moment(newShift.end_time.slice(0, 5), 'HH:mm');
      const sStart = moment(s.start_time.slice(0, 5), 'HH:mm');
      const sEnd = moment(s.end_time.slice(0, 5), 'HH:mm');

      // Handle overnight shifts if any (assuming end time < start time means next day)
      if (nEnd.isBefore(nStart)) nEnd.add(1, 'days');
      if (sEnd.isBefore(sStart)) sEnd.add(1, 'days');

      return (
        (nStart.isSameOrAfter(sStart) && nStart.isBefore(sEnd)) ||
        (nEnd.isAfter(sStart) && nEnd.isSameOrBefore(sEnd)) ||
        (nStart.isSameOrBefore(sStart) && nEnd.isSameOrAfter(sEnd))
      );
    });
  }

  getDefaultShopForEmployeeAndDate(employeeId: number, dateStr: string): any {
    if (!this.shopEmployees || this.shopEmployees.length === 0) {
      return null;
    }

    const targetDate = moment(dateStr, 'YYYY-MM-DD');
    const userShopIds = (this.userShops || []).map((s: any) => Number(s.id));

    const matches = this.shopEmployees.filter((se: any) => {
      if (Number(se.employee_id) !== Number(employeeId)) return false;
      if (Number(se.is_deleted) !== 0) return false;
      if (!se.end_date && Number(se.is_active) !== 1) return false;

      // ONLY consider shops that the logged-in user manages/has access to!
      if (userShopIds.length > 0 && !userShopIds.includes(Number(se.shop_id))) return false;

      // Validate start_date
      if (se.start_date) {
        const start = moment(se.start_date, 'YYYY-MM-DD');
        if (targetDate.isBefore(start, 'day')) return false;
      }

      // Validate end_date
      if (se.end_date) {
        const end = moment(se.end_date, 'YYYY-MM-DD');
        if (targetDate.isAfter(end, 'day')) return false;
      }

      return true;
    });

    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];

    // If more than one record, take is_primary = 1
    const primary = matches.find((se: any) => Number(se.is_primary) === 1);
    return primary || matches[0];
  }

  onDrop(event: CdkDragDrop<any>) {
    if (event.previousContainer === event.container) {
      return;
    }

    const originalShift = event.item.data;
    if (!originalShift || originalShift.type === 'day') return;
    const targetData = event.container.data; // { emp: Employee, date: string }
    if (!targetData || !targetData.emp || !targetData.date) return;

    const origDate = originalShift.shift_date ? originalShift.shift_date.slice(0, 10) : '';
    const targetDate = targetData.date ? targetData.date.slice(0, 10) : '';

    // Prevent duplication in the same cell
    if (Number(originalShift.employee_id) === Number(targetData.emp.id) && origDate === targetDate) {
      return;
    }

    if (this.isEmployeeOnLeave(targetData.emp.id, targetData.date)) {
      this.snackBarService.open(`Cannot add shift: Employee is on leave on ${targetData.date}`, 'error');
      return;
    }

    if (!this.canAddShift(targetData.emp.id, targetData.date)) {
      this.snackBarService.open(`Limit reached: Only ${this.rosterSettings.no_of_shifts || this.rosterSettings.no_of_shift} shifts allowed per day for this employee.`, 'error');
      return;
    }

    // Get the default shop of the employee on the target date from shop_employees
    const defaultShop = this.getDefaultShopForEmployeeAndDate(targetData.emp.id, targetData.date);
    if (!defaultShop) {
      this.snackBarService.open(`Cannot add shift: Employee has no active shop assignment on ${targetData.date}`, 'error');
      return;
    }

    // Create a NEW shift (COPY) instead of moving
    const newShift: Shift = {
      ...originalShift,
      id: -(Math.floor(Math.random() * 1000000)), // Temp ID for new shift
      employee_id: targetData.emp.id,
      shift_date: targetDate,
      is_readonly: false,
      shop_id: defaultShop.shop_id,
      shop_code: defaultShop.shop_code,
      roster_shop_id: originalShift.roster_shop_id || this.selectedShopIds[0] || this.currentUser?.shop_id,
      employee_type_id: targetData.emp.employee_type_id || null,
      job_position_id: targetData.emp.job_position_id || null,
      job_title_id: targetData.emp.job_title_id || null,
      is_dirty: true
    };

    delete (newShift as any).attendance_id;
    delete (newShift as any).act_shift_start_time;
    delete (newShift as any).act_shift_end_time;
    delete (newShift as any).upb;
    delete (newShift as any).entry_reason;
    delete (newShift as any).entry_reason_others;
    delete (newShift as any).is_unscheduled;

    if (this.hasOverlap(newShift)) {
      const dialogData = new ConfirmDialogModel('Overlap Warning', 'This employee already has a shift that overlaps during this time.');
      dialogData.showNoButton = false;
      dialogData.yesButtonCaption = 'OK';
      this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: dialogData
      });
      return;
    }

    // Add to local shifts array
    this.lastEditedShiftId = newShift.id ?? null;
    this.shifts.push(newShift);
    this.hasUnsavedChanges = true;
    this.saveRoster(true);
  }

  onDayDrop(event: CdkDragDrop<any>) {
    if (!event.item.data || event.item.data.type !== 'day') return;

    const sourceDate = event.item.data.date;
    const targetDate = event.container.data.date;

    if (sourceDate === targetDate) return;

    const dialogData = new ConfirmDialogModel('Copy Roster', `Are you sure you want to copy the roster from ${sourceDate} to ${targetDate}? This will overwrite existing shifts on the target date.`);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        // Copy LOCAL shifts from source date BEFORE modifying the array
        const sourceShifts = this.shifts.filter(s => s.shift_date === sourceDate && !s.is_readonly && !s.is_deleted).map(s => ({ ...s }));

        // Remove existing LOCAL shifts on target date (keep external ones)
        this.shifts.forEach(s => {
          if (s.shift_date === targetDate && !s.is_readonly) {
            if (Number(s.id) < 0) {
              s.is_temp_deleted = true;
            } else {
              s.is_deleted = true;
              s.is_dirty = true;
            }
          }
        });
        this.shifts = this.shifts.filter(s => !s.is_temp_deleted);

        let skipCount = 0;
        sourceShifts.forEach((shift, index) => {
          const {
            id,
            task1_code,
            task2_code,
            task3_code,
            is_readonly,
            attendance_id,
            act_shift_start_time,
            act_shift_end_time,
            upb,
            entry_reason,
            entry_reason_others,
            is_unscheduled,
            ...shiftDataToCopy
          } = shift as any;

          if (is_unscheduled && (!shift.start_time || !shift.end_time)) {
            return;
          }

          const newShift: Shift = {
            ...shiftDataToCopy,
            id: -(Math.floor(Math.random() * 1000000)), // Temp ID
            shift_date: targetDate,
            is_dirty: true
          } as any;

          if (!this.hasOverlap(newShift) && this.canAddShift(newShift.employee_id, newShift.shift_date, newShift.shop_id)) {
            this.shifts.push(newShift);
          } else {
            skipCount++;
          }
        });

        if (skipCount > 0) {
          this.snackBarService.open(`Roster copied to ${targetDate}. ${skipCount} shifts skipped due to overlaps`, 'error');
        } else {
          this.snackBarService.open(`Roster copied to ${targetDate}`, 'success');
        }
        this.hasUnsavedChanges = true;
        this.saveRoster(true);
      }
    });
  }

  getEmployeeTotalHours(employeeId: number) {
    const empShifts = this.shifts.filter(s => s.employee_id === employeeId && !s.is_deleted);
    let totalMinutes = 0;
    empShifts.forEach(s => {
      if (s.start_time && s.end_time) {
        const start = moment(s.start_time, 'HH:mm');
        const end = moment(s.end_time, 'HH:mm');
        let diff = end.diff(start, 'minutes');
        if (diff < 0) diff += 24 * 60;
        totalMinutes += diff;
      }
    });
    return totalMinutes / 60;
  }

  hasMultiShopShifts(employeeId: number): boolean {
    return this.getUniqueShopCount(employeeId) > 1;
  }

  getUniqueShopCount(employeeId: number): number {
    if (!this.shifts) return 0;
    const shopIds = new Set<number>();
    for (const s of this.shifts) {
      if (Number(s.employee_id) === Number(employeeId) && !s.is_deleted) {
        const shopId = Number(s.shop_id || s.roster_shop_id || 0);
        if (shopId > 0) {
          shopIds.add(shopId);
        }
      }
    }
    return shopIds.size;
  }

  getOtherShopCount(employeeId: number): number {
    const totalShops = this.getUniqueShopCount(employeeId);
    return totalShops > 1 ? totalShops - 1 : 0;
  }

  getRosterSegments(employeeId: number, totalPercentage: number): Array<{ shopId: number, width: number, color: string }> {
    if (!this.shifts) return [];

    const shopMinutes: { [shopId: number]: number } = {};
    let grandTotalMinutes = 0;

    const empShifts = this.shifts.filter(s => Number(s.employee_id) === Number(employeeId) && !s.is_deleted);
    empShifts.forEach(s => {
      if (s.start_time && s.end_time) {
        const start = moment(s.start_time, 'HH:mm');
        const end = moment(s.end_time, 'HH:mm');
        let diff = end.diff(start, 'minutes');
        if (diff < 0) diff += 24 * 60;

        const shopId = Number(s.shop_id || s.roster_shop_id || 0);
        if (shopId > 0) {
          shopMinutes[shopId] = (shopMinutes[shopId] || 0) + diff;
          grandTotalMinutes += diff;
        }
      }
    });

    if (grandTotalMinutes === 0) return [];

    const sortedShopIds = Object.keys(shopMinutes).map(Number).sort((a, b) => a - b);

    const colors = [
      '#319795', // Teal
      '#ff8c00', // Orange
      '#805ad5', // Purple
      '#3182ce', // Blue
      '#e53e3e', // Red
      '#d69e2e', // Yellow
    ];

    return sortedShopIds.map((shopId, index) => {
      const share = shopMinutes[shopId] / grandTotalMinutes;
      const segmentWidth = share * totalPercentage;
      const color = colors[index % colors.length];
      return {
        shopId,
        width: segmentWidth,
        color
      };
    });
  }

  onRosterBadgeClick(event: Event, employee: any) {
    if (this.hasMultiShopShifts(employee.id)) {
      event.stopPropagation();
      this.showMultiShopDetails(employee);
    }
  }

  showMultiShopDetails(employee: any) {
    if (!this.shifts) return;
    let sessionShopList: any[] = [];
    try {
      const stored = sessionStorage.getItem(AppConstants.SESSION_VAR_SHOP_LIST);
      if (stored) {
        sessionShopList = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error parsing shop list from session storage', e);
    }
    const shopHoursMap: { [shopName: string]: number } = {};
    for (const s of this.shifts) {
      if (Number(s.employee_id) === Number(employee.id) && !s.is_deleted) {
        if (s.start_time && s.end_time) {
          const start = moment(s.start_time, 'HH:mm');
          const end = moment(s.end_time, 'HH:mm');
          let diff = end.diff(start, 'minutes');
          if (diff < 0) diff += 24 * 60;

          const shopId = Number(s.shop_id || s.roster_shop_id || 0);
          let shop = this.userShops.find(sh => Number(sh.id) === shopId);
          if (!shop && sessionShopList.length > 0) {
            shop = sessionShopList.find(sh => Number(sh.id) === shopId);
          }
          const shopName = shop ? shop.name : (s.shop_code || `Shop #${shopId}`);

          shopHoursMap[shopName] = (shopHoursMap[shopName] || 0) + (diff / 60);
        }
      }
    }

    const entries = Object.entries(shopHoursMap);
    if (entries.length === 0) return;

    const title = `${this.getEmployeeDisplayName(employee)} - Rostered Shops`;
    let message = '<div style="font-size: 0.95rem; line-height: 1.6; margin-bottom: 8px; font-weight: 500;">This employee is scheduled to work at the following shops this week:</div>';
    message += '<ul style="margin: 0; padding-left: 20px; font-weight: 600; color: #1e3a8a;">';
    entries.forEach(([shopName, hours]) => {
      message += `<li style="margin-bottom: 6px; display: flex; justify-content: space-between; align-items: center; width: 100%;">`;
      message += `<span>${shopName}:</span>`;
      message += `<span style="color: #2d3748; font-weight: 700; text-align: right; margin-left: 16px;">${hours.toFixed(2)} hrs</span>`;
      message += `</li>`;
    });
    message += '</ul>';

    const dialogData = new ConfirmDialogModel(title, message);
    dialogData.showNoButton = false;
    dialogData.yesButtonCaption = 'Close';

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });
  }

  getEmployeeFilteredHours(employeeId: number) {
    let empShifts = this.shifts.filter(s => s.employee_id === employeeId && !s.is_deleted);

    // Determine the target shop IDs to filter by
    let targetShopIds: number[] = [];

    if (this.currentUser?.user_type === 'SHOP') {
      // Shop login: only show this shop's hours
      const shopId = Number(this.currentUser.shop_id);
      if (shopId) {
        targetShopIds = [shopId];
      }
    } else if (this.currentUser?.user_type === 'AREA_MANAGER') {
      // Area Manager login: only consider assigned shops that are currently selected
      const assignedShopIds = (this.userShops || []).map(s => Number(s.id));
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        const selected = this.selectedShopIds.map(id => Number(id));
        targetShopIds = selected.filter(id => assignedShopIds.includes(id));
      } else {
        targetShopIds = assignedShopIds;
      }
    } else {
      // HQ/other logins: show hours of the selected shops
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        targetShopIds = this.selectedShopIds.map(id => Number(id));
      }
    }

    if (targetShopIds.length > 0) {
      empShifts = empShifts.filter(s => {
        const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
        return sShopId && targetShopIds.includes(sShopId);
      });
    }

    let totalMinutes = 0;
    empShifts.forEach(s => {
      if (s.start_time && s.end_time) {
        const start = moment(s.start_time, 'HH:mm');
        const end = moment(s.end_time, 'HH:mm');
        let diff = end.diff(start, 'minutes');
        if (diff < 0) diff += 24 * 60;
        totalMinutes += diff;
      }
    });
    return totalMinutes / 60;
  }

  getEmployeeTotalCost(employee: Employee) {
    const hours = this.getEmployeeTotalHours(employee.id);
    return hours * employee.cost_per_hour;
  }

  getEmployeeFilteredCost(employee: Employee) {
    const hours = this.getEmployeeFilteredHours(employee.id);
    return hours * employee.cost_per_hour;
  }

  getComplianceStatus(emp: Employee) {
    const totalHours = this.getEmployeeTotalHours(emp.id);
    const min = emp.min_work_hours || 0;
    const max = emp.max_work_hours != null ? Number(emp.max_work_hours) : 0;

    let colorClass = 'compliance-green';
    let status = 'ok';
    let message = 'Within hour limits';

    if (totalHours === 0) {
      // No shifts assigned: status is ok and no warnings/indicators should be triggered
      colorClass = 'compliance-green';
      status = 'ok';
      message = 'Within hour limits';
    } else if (totalHours < min) {
      colorClass = 'compliance-orange';
      status = 'under';
      message = `Under minimum (${min} hrs)`;
    } else if (max > 0 && totalHours > max) {
      colorClass = 'compliance-red';
      status = 'over';
      message = `Exceeds maximum (${max} hrs)`;
    }

    const percentage = max > 0 ? (totalHours / max) * 100 : (min > 0 ? (totalHours / min) * 100 : 0);

    return {
      colorClass,
      status,
      message,
      percentage: Math.min(percentage, 100),
      totalHours,
      min,
      max
    };
  }

  getEmployeeActualFilteredHours(employeeId: number): number {
    let empShifts = this.shifts.filter(s => s.employee_id === employeeId && !s.is_deleted && !s.is_attendance_deleted);

    // Resolve target shop IDs for the active view to filter shifts
    let targetShopIds: number[] = [];
    if (this.currentUser?.user_type === 'SHOP') {
      if (this.currentUser.shop_id) {
        targetShopIds = [Number(this.currentUser.shop_id)];
      }
    } else if (this.currentUser?.user_type === 'AREA_MANAGER') {
      const assignedShopIds = (this.userShops || []).map(s => Number(s.id));
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        const selected = this.selectedShopIds.map(id => Number(id));
        targetShopIds = selected.filter(id => assignedShopIds.includes(id));
      } else {
        targetShopIds = assignedShopIds;
      }
    } else {
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        targetShopIds = this.selectedShopIds.map(id => Number(id));
      }
    }

    if (targetShopIds.length > 0) {
      empShifts = empShifts.filter(s => {
        const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
        return sShopId && targetShopIds.includes(sShopId);
      });
    }

    let totalMinutes = 0;
    empShifts.forEach(shift => {
      if (!shift.act_shift_start_time || shift.act_shift_start_time === '' || shift.act_shift_start_time === '00:00' || shift.act_shift_start_time === '00:00:00' ||
        !shift.act_shift_end_time || shift.act_shift_end_time === '' || shift.act_shift_end_time === '00:00' || shift.act_shift_end_time === '00:00:00') return;
      const start = moment(shift.act_shift_start_time, 'HH:mm');
      const end = moment(shift.act_shift_end_time, 'HH:mm');
      let durationMinutes = end.diff(start, 'minutes');
      if (durationMinutes < 0) durationMinutes += 24 * 60;

      if (shift.upb) {
        const upbParts = shift.upb.split(':');
        if (upbParts.length >= 2) {
          const upbMinutes = parseInt(upbParts[0], 10) * 60 + parseInt(upbParts[1], 10);
          durationMinutes = Math.max(0, durationMinutes - upbMinutes);
        }
      }
      totalMinutes += durationMinutes;
    });

    return totalMinutes / 60;
  }

  getEmployeeActualFilteredCost(employee: Employee): number {
    const hours = this.getEmployeeActualFilteredHours(employee.id);
    return hours * employee.cost_per_hour;
  }

  getActualComplianceStatus(emp: Employee) {
    const actualDecimalHours = this.getEmployeeActualFilteredHours(emp.id);
    const rosteredHours = this.getEmployeeFilteredHours(emp.id);

    let colorClass = 'compliance-green';
    let status = 'ok';

    if (actualDecimalHours < rosteredHours) {
      colorClass = 'compliance-red';
      status = 'under';
    } else if (actualDecimalHours > rosteredHours) {
      colorClass = 'compliance-red';
      status = 'over';
    }

    const percentage = rosteredHours > 0 ? (actualDecimalHours / rosteredHours) * 100 : 0;

    return {
      colorClass,
      status,
      percentage: Math.min(percentage, 100),
      totalHours: actualDecimalHours,
      max: rosteredHours
    };
  }

  showComplianceMessage(emp: Employee, compliance: any) {
    if (compliance.status === 'ok') {
      return;
    }
    const empName = this.getEmployeeDisplayName(emp);
    const dialogData = new ConfirmDialogModel(
      'Rostered Hours Warning',
      `Warning: <strong>${empName}</strong> - ${compliance.message}.`
    );
    dialogData.showNoButton = false;
    dialogData.yesButtonCaption = 'OK';
    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });
  }

  hasConsecutiveWorkDays(employeeId: number, daysCount: number = 6): boolean {
    const workedDates = new Set<string>();
    this.shifts.forEach(s => {
      if (s.employee_id === employeeId && !s.is_deleted) {
        workedDates.add(s.shift_date);
      }
    });

    const sortedDays = this.weekDays.map(d => d.date);

    for (let i = 0; i <= sortedDays.length - daysCount; i++) {
      let isConsecutive = true;
      for (let j = 0; j < daysCount; j++) {
        if (!workedDates.has(sortedDays[i + j])) {
          isConsecutive = false;
          break;
        }
      }
      if (isConsecutive) return true;
    }

    return false;
  }

  getTargetShopIds(): number[] {
    let targetShopIds: number[] = [];
    if (this.currentUser?.user_type === 'SHOP') {
      const shopId = Number(this.currentUser.shop_id);
      if (shopId) {
        targetShopIds = [shopId];
      }
    } else if (this.currentUser?.user_type === 'AREA_MANAGER') {
      const assignedShopIds = (this.userShops || []).map(s => Number(s.id));
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        const selected = this.selectedShopIds.map(id => Number(id));
        targetShopIds = selected.filter(id => assignedShopIds.includes(id));
      } else {
        targetShopIds = assignedShopIds;
      }
    } else {
      if (this.selectedShopIds && this.selectedShopIds.length > 0) {
        targetShopIds = this.selectedShopIds.map(id => Number(id));
      }
    }
    return targetShopIds;
  }

  getDailyStats(date: string) {
    let dayShifts = this.shifts.filter(s => s.shift_date === date && !s.is_deleted);

    const targetShopIds = this.getTargetShopIds();

    if (targetShopIds.length > 0) {
      dayShifts = dayShifts.filter(s => {
        const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
        return sShopId && targetShopIds.includes(sShopId);
      });
    }

    let totalMinutes = 0;
    let totalCost = 0;
    let actualMinutes = 0;
    let actualCost = 0;
    const empIds = new Set();

    dayShifts.forEach(s => {
      empIds.add(s.employee_id);
      const emp = this.employees.find(e => e.id === s.employee_id);

      if (s.start_time && s.end_time) {
        const start = moment(s.start_time, 'HH:mm');
        const end = moment(s.end_time, 'HH:mm');
        let duration = end.diff(start, 'minutes');
        if (duration < 0) duration += 24 * 60;
        totalMinutes += duration;
        if (emp) {
          totalCost += (duration / 60) * emp.cost_per_hour;
        }
      }

      // Actuals calculation
      if (s.is_finalized && !s.is_attendance_deleted &&
        s.act_shift_start_time && s.act_shift_start_time !== '' && s.act_shift_start_time !== '00:00' && s.act_shift_start_time !== '00:00:00' &&
        s.act_shift_end_time && s.act_shift_end_time !== '' && s.act_shift_end_time !== '00:00' && s.act_shift_end_time !== '00:00:00') {
        const actStart = moment(s.act_shift_start_time, 'HH:mm');
        const actEnd = moment(s.act_shift_end_time, 'HH:mm');
        let actMins = actEnd.diff(actStart, 'minutes');
        if (actMins < 0) actMins += 24 * 60;

        if (s.upb) {
          const upbParts = s.upb.split(':');
          if (upbParts.length >= 2) {
            const upbMinutes = parseInt(upbParts[0], 10) * 60 + parseInt(upbParts[1], 10);
            actMins = Math.max(0, actMins - upbMinutes);
          }
        }
        actualMinutes += actMins;
        if (emp) {
          actualCost += (actMins / 60) * emp.cost_per_hour;
        }
      }
    });

    const dayKey = moment(date).format('ddd').toLowerCase();
    const salesForecast = this.header ? (Number(this.header[`${dayKey}_sales_forecast`]) || 0) : 0;
    const wageTarget = this.header ? (Number(this.header[`${dayKey}_wage_target`]) || 0) : 0;
    const rosteredWagePct = salesForecast > 0 ? (totalCost / salesForecast) * 100 : 0;
    const variance = rosteredWagePct - wageTarget;

    const actualSales = this.actualSales ? (Number(this.actualSales[dayKey]) || 0) : 0;
    const actualHours = actualMinutes / 60;
    const actualWagePct = actualSales > 0 ? (actualCost / actualSales) * 100 : 0;
    const actualVariance = actualWagePct - wageTarget;

    return {
      empCount: empIds.size,
      hours: totalMinutes / 60,
      cost: totalCost,
      salesForecast,
      wageTarget,
      rosteredWagePct,
      variance,
      actualHours,
      actualCost,
      actualSales,
      actualWagePct,
      actualVariance
    };
  }

  getMissingOpenerShops(date: string): { id: number; code: string }[] {
    let dailyShifts = this.shifts.filter(s => s.shift_date === date && !s.is_deleted);
    if (dailyShifts.length === 0) return [];

    const targetShopIds = this.getTargetShopIds();
    if (targetShopIds.length > 0) {
      dailyShifts = dailyShifts.filter(s => {
        const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
        return sShopId && targetShopIds.includes(sShopId);
      });
    }
    if (dailyShifts.length === 0) return [];

    const userShopIds = new Set((this.userShops || []).map(s => Number(s.id)));

    // Find all unique shops that have shifts on this date and are assigned to the user
    const uniqueShopsMap = new Map<number, string>();
    dailyShifts.forEach(s => {
      const shopId = Number(s.shop_id);
      if (s.shop_id && userShopIds.has(shopId)) {
        uniqueShopsMap.set(shopId, s.shop_code || `Shop #${s.shop_id}`);
      }
    });

    const missingShops: { id: number; code: string }[] = [];

    uniqueShopsMap.forEach((shopCode, shopId) => {
      const shopShifts = dailyShifts.filter(s => Number(s.shop_id) === shopId);
      if (shopShifts.length === 0) return;

      const hasIC = shopShifts.some(s => !!s.is_in_charge);

      if (!hasIC) {
        missingShops.push({ id: shopId, code: shopCode });
      }
    });

    return missingShops;
  }

  isMissingOpenerIC(date: string): boolean {
    return this.getMissingOpenerShops(date).length > 0;
  }

  getDayHeaderStatus(date: string): 'pending' | 'ready' | 'exceptions' | 'finalised' | 'empty' {
    if (!this.isPastOrCurrentDate(date)) {
      return 'pending';
    }

    let dayShifts = this.shifts.filter(s => s.shift_date === date && !s.is_deleted);
    const targetShopIds = this.getTargetShopIds();
    if (targetShopIds.length > 0) {
      dayShifts = dayShifts.filter(s => {
        const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
        return sShopId && targetShopIds.includes(sShopId);
      });
    }

    if (dayShifts.length === 0) {
      return 'empty';
    }

    const allFinalised = dayShifts.every(s => s.is_finalized || s.is_absent);
    if (allFinalised) {
      return 'finalised';
    }

    let hasUnresolved = false;
    for (const shift of dayShifts) {
      const status = this.getShiftStatus(shift);
      if (status === 'red' || status === 'orange' || status === 'purple') {
        if (!this.isShiftReviewed(shift)) {
          hasUnresolved = true;
          break;
        }
      }
    }

    return hasUnresolved ? 'exceptions' : 'ready';
  }

  handleDayHeaderClick(day: any) {
    const status = this.getDayHeaderStatus(day.date);
    if (status === 'empty') {
      return;
    }

    if (status === 'ready') {
      if (this.hasUnsavedChanges) {
        this.saveRoster(true, () => {
          this.handleDayHeaderClick(day);
        });
        return;
      }

      let dayShifts = this.shifts.filter(s => s.shift_date === day.date && !s.is_deleted);
      const targetShopIds = this.getTargetShopIds();
      if (targetShopIds.length > 0) {
        dayShifts = dayShifts.filter(s => {
          const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
          return sShopId && targetShopIds.includes(sShopId);
        });
      }

      // Check for missing attendances without marking absent
      const missingAttendances = dayShifts.some(s =>
        !s.is_unscheduled &&
        !s.is_absent &&
        (!s.act_shift_start_time || s.act_shift_start_time === '' || s.act_shift_start_time === '00:00' || s.act_shift_start_time === '00:00:00' ||
          !s.act_shift_end_time || s.act_shift_end_time === '' || s.act_shift_end_time === '00:00' || s.act_shift_end_time === '00:00:00')
      );
      if (missingAttendances) {
        this.snackBarService.open('Cannot finalise day: Please ensure all shifts have actual attendance or are marked as absent.', 'error');
        return;
      }

      const toFinalise = dayShifts.filter(s => s.attendance_id && !s.is_finalized);

      if (toFinalise.length === 0) {
        this.snackBarService.open('No shifts with attendance to finalise.', 'info');
        return;
      }

      const missingIC = this.isMissingOpenerIC(day.date);
      let message = `Are you sure you want to finalise shifts for ${day.fullDisplay}?`;
      if (missingIC) {
        message = `<span style="color: #e53e3e; font-weight: bold; display: block; margin-bottom: 10px;">Warning: The shop is missing an In-Charge task on this day.</span>` + message;
      }

      const dialogData = new ConfirmDialogModel('Finalise Day', message);
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        data: dialogData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const payload = {
            attendances: toFinalise.map(s => ({
              attendance_id: s.attendance_id,
              shop_id: s.shop_id
            })),
            is_finalized: 1
          };
          this.rosterService.bulkFinalizeAttendance(payload).subscribe({
            next: (response) => {
              if (response.status === 'SUCCESS') {
                toFinalise.forEach(s => s.is_finalized = true);
                this.snackBarService.open(`Day ${day.fullDisplay} finalised successfully.`, 'success');
              } else {
                this.snackBarService.open(response.message || 'Failed to finalise day.', 'error');
              }
            },
            error: (err) => {
              console.error('Error finalising day', err);
              this.snackBarService.open('Error finalising day.', 'error');
            }
          });
        }
      });
    } else if (status === 'finalised') {
      const isAuthorized = this.currentUser?.user_type === 'HQ' || this.currentUser?.user_type === 'AREA_MANAGER';
      if (!isAuthorized) {
        this.snackBarService.open('You are not authorized to reopen finalised days.', 'error');
        return;
      }

      let dayShifts = this.shifts.filter(s => s.shift_date === day.date && !s.is_deleted);
      const targetShopIds = this.getTargetShopIds();
      if (targetShopIds.length > 0) {
        dayShifts = dayShifts.filter(s => {
          const sShopId = Number(s.shop_id || s.roster_shop_id || 0);
          return sShopId && targetShopIds.includes(sShopId);
        });
      }
      const toReopen = dayShifts.filter(s => s.attendance_id && s.is_finalized);

      if (toReopen.length === 0) {
        return;
      }

      const dialogData = new ConfirmDialogModel(
        'Reopen Day',
        `Are you sure you want to reopen shifts for ${day.fullDisplay}?`
      );
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: dialogData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const payload = {
            attendances: toReopen.map(s => ({
              attendance_id: s.attendance_id,
              shop_id: s.shop_id
            })),
            is_finalized: 0
          };
          this.rosterService.bulkFinalizeAttendance(payload).subscribe({
            next: (response) => {
              if (response.status === 'SUCCESS') {
                toReopen.forEach(s => s.is_finalized = false);
                this.snackBarService.open(`Day ${day.fullDisplay} reopened successfully.`, 'success');
              } else {
                this.snackBarService.open(response.message || 'Failed to reopen day.', 'error');
              }
            },
            error: (err) => {
              console.error('Error reopening day', err);
              this.snackBarService.open('Error reopening day.', 'error');
            }
          });
        }
      });
    } else if (status === 'exceptions') {
      const dialogData = new ConfirmDialogModel(
        'Cannot Finalise Day',
        `This day has unresolved exceptions (missing clock times or shifts outside thresholds). Please review and resolve all exceptions (add adjustment reasons) before finalising.`
      );
      dialogData.showNoButton = false;
      dialogData.yesButtonCaption = 'OK';
      this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        data: dialogData
      });
    } else {
      this.snackBarService.open('No shifts to finalise or not a past/current date.', 'info');
    }
  }

  public ngOnDestroy(): void {
    this.headerMetricSubject.complete();
  }

  updateHeaderMetric(dayKey: string, field: 'sales_forecast' | 'wage_target', value: any) {
    if (!this.header) return;
    if (field === 'wage_target' && !this.canEditWagesCost) return;
    if (field === 'sales_forecast' && !this.canEditSales) return;
    this.header[`${dayKey}_${field}`] = Number(value);
    this.hasUnsavedChanges = true;
    this.headerMetricSubject.next();
  }

  onHeaderMetricBlur() {
    if (this.hasUnsavedChanges) {
      this.saveRoster(true);
    }
  }

  getWeeklyGrandTotal() {
    let totalHours = 0;
    let totalCost = 0;
    let totalSales = 0;
    let totalTarget = 0;

    let totalActualHours = 0;
    let totalActualCost = 0;
    let totalActualSales = 0;

    this.weekDays.forEach(day => {
      const stats = this.getDailyStats(day.date);
      totalHours += stats.hours;
      totalCost += stats.cost;
      totalSales += stats.salesForecast;
      totalTarget += stats.wageTarget;

      totalActualHours += stats.actualHours;
      totalActualCost += stats.actualCost;
      totalActualSales += stats.actualSales;
    });

    const avgWagePct = totalSales > 0 ? (totalCost / totalSales) * 100 : 0;
    const avgTargetPct = this.weekDays.length > 0 ? (totalTarget / this.weekDays.length) : 0;

    const avgActualWagePct = totalActualSales > 0 ? (totalActualCost / totalActualSales) * 100 : 0;
    const avgActualVariance = avgActualWagePct - avgTargetPct;

    return {
      hours: totalHours,
      cost: totalCost,
      sales: totalSales,
      wagePct: avgWagePct,
      targetPct: avgTargetPct,
      variance: avgWagePct - avgTargetPct,

      actualHours: totalActualHours,
      actualCost: totalActualCost,
      actualSales: totalActualSales,
      actualWagePct: avgActualWagePct,
      actualVariance: avgActualVariance
    };
  }

  getDayKey(date: string) {
    return moment(date).format('ddd').toLowerCase();
  }

  openRosterList() {
    const dialogRef = this.dialog.open(RosterListComponent, {
      width: '800px',
      maxHeight: '90vh',
      minHeight: '180px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.week_number && result.year) {
        this.confirmIfDirty(() => {
          this.currentWeekNo = Number(result.week_number);
          this.currentYear = Number(result.year);
          const weekDef = this.weekDefs.find(w => Number(w.week_no) === this.currentWeekNo && Number(w.year) === this.currentYear);
          if (weekDef) {
            this.currentDate = moment(weekDef.start_date);
          } else {
            this.currentDate = moment().year(this.currentYear).isoWeek(this.currentWeekNo).startOf('isoWeek');
          }

          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              week_number: this.currentWeekNo,
              year: this.currentYear
            },
            queryParamsHandling: 'merge'
          });

          this.generateWeekDays();
          this.loadRoster();
        }, 'You have unsaved changes in this roster. If you change the roster, your unsaved changes will be lost. Do you want to proceed?');
      }
    });
  }

  openSelectWeekDialog() {
    const dialogRef = this.dialog.open(SelectWeekDialogComponent, {
      width: '750px',
      data: {
        weekDefs: this.weekDefs,
        currentWeekNo: this.currentWeekNo,
        currentYear: this.currentYear
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.confirmIfDirty(() => {
          this.currentWeekNo = Number(result.week_no);
          this.currentYear = Number(result.year);
          this.currentDate = moment(result.start_date);

          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              week_number: this.currentWeekNo,
              year: this.currentYear
            },
            queryParamsHandling: 'merge'
          });

          this.generateWeekDays();
          this.loadRoster();
        }, 'You have unsaved changes in this roster. If you change the week, your unsaved changes will be lost. Do you want to proceed?');
      }
    });
  }

  nextWeek() {
    this.confirmIfDirty(() => {
      const idx = this.weekDefs.findIndex(w => Number(w.week_no) === this.currentWeekNo && Number(w.year) === this.currentYear);
      if (idx >= 0 && idx < this.weekDefs.length - 1) {
        const nextDef = this.weekDefs[idx + 1];
        this.currentWeekNo = Number(nextDef.week_no);
        this.currentYear = Number(nextDef.year);
        this.currentDate = moment(nextDef.start_date);
      } else {
        this.currentDate.add(1, 'week');
        this.currentWeekNo = this.currentDate.isoWeek();
        this.currentYear = this.currentDate.year();
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          week_number: this.currentWeekNo,
          year: this.currentYear
        },
        queryParamsHandling: 'merge'
      });

      this.generateWeekDays();
      this.loadRoster();
    }, 'You have unsaved changes in this roster. If you navigate to the next week, your unsaved changes will be lost. Do you want to proceed?');
  }

  prevWeek() {
    this.confirmIfDirty(() => {
      const idx = this.weekDefs.findIndex(w => Number(w.week_no) === this.currentWeekNo && Number(w.year) === this.currentYear);
      if (idx > 0) {
        const prevDef = this.weekDefs[idx - 1];
        this.currentWeekNo = Number(prevDef.week_no);
        this.currentYear = Number(prevDef.year);
        this.currentDate = moment(prevDef.start_date);
      } else {
        this.currentDate.subtract(1, 'week');
        this.currentWeekNo = this.currentDate.isoWeek();
        this.currentYear = this.currentDate.year();
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          week_number: this.currentWeekNo,
          year: this.currentYear
        },
        queryParamsHandling: 'merge'
      });

      this.generateWeekDays();
      this.loadRoster();
    }, 'You have unsaved changes in this roster. If you navigate to the previous week, your unsaved changes will be lost. Do you want to proceed?');
  }

  getTaskColor(taskId: number, type: 'bg' | 'fg') {
    const task = this.tasks.find(t => t.id == taskId);
    if (!task) return type === 'bg' ? '#eee' : '#000';
    return type === 'bg' ? task.bg_color : task.fg_color;
  }

  getTaskCode(taskId: number) {
    const task = this.tasks.find(t => t.id == taskId);
    return task ? task.code : '';
  }

  getShiftCode(shift: Shift) {
    const template = this.masterShifts.find(s =>
      s.start_time.slice(0, 5) === shift.start_time.slice(0, 5) &&
      s.end_time.slice(0, 5) === shift.end_time.slice(0, 5)
    );
    return template ? template.code : 'CUST';
  }

  openShiftModal(employee: Employee, date: string, shift?: Shift) {
    if (this.header?.is_submitted == 1) {
      if (!shift) {
        this.snackBarService.open('Roster is submitted and cannot be modified.', 'error');
        return;
      }
      shift = { ...shift, is_readonly: true };
    } else if (!shift && !this.canAddShift(employee.id, date)) {
      this.snackBarService.open(`Limit reached: Only ${this.rosterSettings.no_of_shifts || this.rosterSettings.no_of_shift} shifts allowed per day for this employee.`, 'error');
      return;
    }

    const dialogRef = this.dialog.open(ShiftDialogComponent, {
      width: '900px',
      data: {
        employee: employee,
        date: date,
        shift: shift,
        is_submitted: this.header?.is_submitted == 1,
        tasks: this.tasks,
        masterShifts: this.masterShifts,
        reasons: this.reasons,
        userShops: this.userShops,
        selectedShopIds: this.selectedShopIds,
        shopEmployees: this.shopEmployees,
        isPastOrCurrentWeek: this.isPastOrCurrentWeek(),
        rosterSettings: this.rosterSettings,
        settingsColors: this.settingsColors,
        canViewAttendance: this.canViewAttendance,
        canEditAttendance: this.canEditAttendance,
        canViewShift: this.canViewShift,
        canEditShift: this.canEditShift
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (this.header?.is_submitted == 1) {
        return;
      }
      if (result) {
        if (result.action === 'save') {
          const updatedShift = result.shift;

          // Check if any actual values changed from the original shift
          if (shift) {
            const hasChanged =
              (updatedShift.start_time || '') !== (shift.start_time || '') ||
              (updatedShift.end_time || '') !== (shift.end_time || '') ||
              Number(updatedShift.shop_id) !== Number(shift.shop_id) ||
              updatedShift.is_in_charge !== shift.is_in_charge ||
              (updatedShift.notes || '') !== (shift.notes || '') ||
              updatedShift.task_id1 !== shift.task_id1 ||
              updatedShift.task_id2 !== shift.task_id2 ||
              updatedShift.task_id3 !== shift.task_id3 ||
              (updatedShift.act_shift_start_time || '') !== (shift.act_shift_start_time || '') ||
              (updatedShift.act_shift_end_time || '') !== (shift.act_shift_end_time || '') ||
              Number(updatedShift.entry_reason) !== Number(shift.entry_reason) ||
              (updatedShift.entry_reason_others || '') !== (shift.entry_reason_others || '');

            if (!hasChanged) {
              return; // No changes made, do not check for overlaps or mark as dirty
            }
          }

          if (this.hasOverlap(updatedShift)) {
            const dialogData = new ConfirmDialogModel('Overlap Warning', 'This employee already has a shift that overlaps during this time.');
            dialogData.showNoButton = false;
            dialogData.yesButtonCaption = 'OK';
            this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: dialogData
            });
            return;
          }

          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) {
              updatedShift.shop_code = shopEmp.shop_code;
            } else {
              const shop = this.userShops.find(s => Number(s.id) === Number(updatedShift.shop_id));
              if (shop) updatedShift.shop_code = shop.code;
            }
          }

          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
              this.lastEditedShiftId = updatedShift.id;
            }
          } else {
            updatedShift.id = -(Math.floor(Math.random() * 1000000));
            updatedShift.is_dirty = true;
            updatedShift.employee_type_id = employee?.employee_type_id || null;
            updatedShift.job_position_id = employee?.job_position_id || null;
            updatedShift.job_title_id = employee?.job_title_id || null;
            this.lastEditedShiftId = updatedShift.id;
            this.shifts.push(updatedShift);
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'save_then_delete') {
          const updatedShift = result.shift;
          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) updatedShift.shop_code = shopEmp.shop_code;
          }
          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
            }
          }
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_deleted = true;
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'save_then_delete_shift') {
          const updatedShift = result.shift;
          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) updatedShift.shop_code = shopEmp.shop_code;
          }
          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
            }
          }
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_shift_deleted = true;
            shiftToDelete.is_unscheduled = true;
            shiftToDelete.start_time = '';
            shiftToDelete.end_time = '';
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
            if (!shiftToDelete.act_shift_start_time && !shiftToDelete.act_shift_end_time && !shiftToDelete.attendance_id) {
              shiftToDelete.is_deleted = true;
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'save_then_delete_attendance') {
          const updatedShift = result.shift;
          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) updatedShift.shop_code = shopEmp.shop_code;
          }
          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
            }
          }
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_attendance_deleted = true;
            shiftToDelete.act_shift_start_time = '';
            shiftToDelete.act_shift_end_time = '';
            shiftToDelete.upb = undefined;
            shiftToDelete.entry_reason = undefined;
            shiftToDelete.entry_reason_others = undefined;
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
            if (shiftToDelete.is_unscheduled || (!shiftToDelete.start_time && !shiftToDelete.end_time)) {
              shiftToDelete.is_deleted = true;
              shiftToDelete.is_shift_deleted = true;
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'save_then_finalize') {
          const updatedShift = result.shift;
          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) updatedShift.shop_code = shopEmp.shop_code;
          }
          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true, () => {
            const match = this.shifts.find(s => s.id == updatedShift.id);
            if (match) {
              this.finalizeAttendance(match);
            }
          });
        } else if (result.action === 'save_then_undo_finalize') {
          const updatedShift = result.shift;
          if (updatedShift.shop_id) {
            const shopEmp = this.shopEmployees.find(se => Number(se.shop_id) === Number(updatedShift.shop_id));
            if (shopEmp) updatedShift.shop_code = shopEmp.shop_code;
          }
          if (updatedShift.id) {
            const index = this.shifts.findIndex(s => s.id == updatedShift.id);
            if (index !== -1) {
              this.shifts[index] = { ...this.shifts[index], ...updatedShift, is_dirty: true };
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true, () => {
            const match = this.shifts.find(s => s.id == updatedShift.id);
            if (match) {
              this.undoFinalizeAttendance(match);
            }
          });
        } else if (result.action === 'delete') {
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_deleted = true;
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'delete_shift') {
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_shift_deleted = true;
            shiftToDelete.is_unscheduled = true;
            shiftToDelete.start_time = '';
            shiftToDelete.end_time = '';
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
            // If it has no attendance, remove it entirely
            if (!shiftToDelete.act_shift_start_time && !shiftToDelete.act_shift_end_time && !shiftToDelete.attendance_id) {
              shiftToDelete.is_deleted = true;
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'delete_attendance') {
          const shiftToDelete = this.shifts.find(s => s.id == result.id);
          if (shiftToDelete) {
            shiftToDelete.is_attendance_deleted = true;
            shiftToDelete.act_shift_start_time = '';
            shiftToDelete.act_shift_end_time = '';
            shiftToDelete.upb = undefined;
            shiftToDelete.entry_reason = undefined;
            shiftToDelete.entry_reason_others = undefined;
            shiftToDelete.is_dirty = true;
            this.lastEditedShiftId = shiftToDelete.id ?? null;
            // If it's unscheduled (no shift), deleting attendance leaves nothing, so remove it entirely
            if (shiftToDelete.is_unscheduled || (!shiftToDelete.start_time && !shiftToDelete.end_time)) {
              shiftToDelete.is_deleted = true;
              shiftToDelete.is_shift_deleted = true;
            }
          }
          this.hasUnsavedChanges = true;
          this.saveRoster(true);
        } else if (result.action === 'finalize') {
          this.finalizeAttendance(result.shift);
        } else if (result.action === 'undo_finalize') {
          this.undoFinalizeAttendance(result.shift);
        }
      }
    });
  }

  saveRoster(silent: boolean = false, callback?: () => void) {
    const payload = {
      silent: silent,
      header: this.header,
      employee_ids: this.employees.map(e => Number(e.id)),
      details: this.shifts
        .filter(s => {
          // Keep all editable shifts, as well as readonly/finalized shifts of the active shop.
          // Exclude readonly shifts of other shops to avoid triggering unwanted updates/deletes on those shops.
          if (s.is_readonly) {
            const rosterShopId = Number(s.roster_shop_id || s.shop_id || 0);
            return this.selectedShopIds.map(Number).includes(rosterShopId);
          }
          if (silent) {
            // Only send dirty, deleted, or modified shifts when silent autosaving
            return s.is_dirty || s.is_deleted || s.is_shift_deleted || s.is_attendance_deleted;
          }
          return true;
        })
        .map(s => {
          const { task1_code, task2_code, task3_code, shop_code, is_dirty, ...rest } = s as any;
          return s.id ? { id: s.id, ...rest } : rest;
        })
    };
    if (!silent) {
      this.isSaving = true;
      this.isLoading = true;
    }
    this.rosterService.saveRoster(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res.status === 'SUCCESS') {
          this.snackBarService.open('Roster saved successfully', 'success');
          this.hasUnsavedChanges = false;

          // Map temporary IDs back to database IDs locally to avoid full reload
          if (res.mappings && res.mappings.length > 0) {
            res.mappings.forEach((m: any) => {
              const match = this.shifts.find(s => s.id == m.temp_id || (m.id && s.id == m.id));
              if (match) {
                if (m.id) {
                  match.id = m.id;
                }
                if (m.attendance_id) {
                  match.attendance_id = m.attendance_id;
                }
              }
            });
          }

          // Clear dirty state flags
          this.shifts.forEach(s => {
            s.is_dirty = false;
          });

          if (callback) {
            callback();
          }

          if (!silent) {
            this.loadRoster();
          } else {
            this.isLoading = false;
          }
        } else {
          this.isLoading = false;
          this.snackBarService.open(res.message || 'Failed to save roster', 'error');
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.isLoading = false;
        this.snackBarService.open('Failed to save roster', 'error');
      }
    });
  }

  submitRoster() {
    // Check if any shifts are missing actual attendance without being marked absent
    const missingAttendances = this.shifts.some(s =>
      !s.is_deleted &&
      !s.is_unscheduled &&
      !s.is_absent &&
      (!s.act_shift_start_time || s.act_shift_start_time === '' || s.act_shift_start_time === '00:00' || s.act_shift_start_time === '00:00:00' ||
        !s.act_shift_end_time || s.act_shift_end_time === '' || s.act_shift_end_time === '00:00' || s.act_shift_end_time === '00:00:00')
    );

    if (missingAttendances) {
      this.snackBarService.open('Cannot submit roster: Please ensure all shifts have actual attendance or are marked as absent.', 'error');
      return;
    }

    // Validation: make sure all entries are finalized (for shifts that have actual attendance start/end times)
    const hasUnfinalized = this.shifts.some(s =>
      !s.is_deleted &&
      !s.is_attendance_deleted &&
      !s.is_temp_deleted &&
      s.attendance_id &&
      s.act_shift_start_time &&
      s.act_shift_end_time &&
      !s.is_finalized
    );

    if (hasUnfinalized) {
      this.snackBarService.open('Cannot submit roster: Please finalize all attendance entries first.', 'error');
      return;
    }

    const dialogData = new ConfirmDialogModel('Submit Roster', 'Are you sure you want to submit this roster? Once submitted, no further edits can be made.');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        if (this.header) {
          this.header.is_submitted = 1;
        }
        this.saveRoster(false);
      }
    });
  }

  print() {
    window.print();
  }

  exportRoster() {
    const hasActiveShifts = this.shifts && this.shifts.some(s => {
      const isPlanned = !s.is_deleted && !s.is_shift_deleted && s.start_time && s.end_time;
      const isActual = !s.is_deleted && !s.is_attendance_deleted && s.act_shift_start_time && s.act_shift_end_time;
      return isPlanned || isActual;
    });

    if (!hasActiveShifts) {
      this.snackBarService.open('No roster records found to export', 'error');
      return;
    }

    const dialogRef = this.dialog.open(RosterExportDialogComponent, {
      width: '450px',
      data: {
        userType: this.currentUser?.user_type || 'SHOP'
      }
    });

    dialogRef.afterClosed().subscribe((selectedType: number) => {
      if (selectedType) {
        this.IsDownloading = true;
        this.rosterService.exportRoster(this.currentWeekNo, this.currentYear, this.selectedShopIds, selectedType).subscribe({
          next: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            let suffix = 'forecast_mgr';
            if (selectedType === 2) suffix = 'forecast_actual_mgr';
            else if (selectedType === 3) suffix = 'forecast_staff';
            else if (selectedType === 4) suffix = 'forecast_actual_staff';
            else if (selectedType === 5) suffix = 'actual_staff';
            else if (selectedType === 6) suffix = 'actual_mgr';

            a.download = `Roster_Week_${this.currentWeekNo}_${this.currentYear}_${suffix}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            this.IsDownloading = false;
          },
          error: (err) => {
            console.error('Export failed:', err);
            if (err.error instanceof Blob) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const errorObj = JSON.parse(reader.result as string);
                  this.snackBarService.open(errorObj.message || 'Export failed', 'error');
                } catch {
                  this.snackBarService.open('Export failed', 'error');
                }
              };
              reader.readAsText(err.error);
            } else {
              this.snackBarService.open('Export failed', 'error');
            }
            this.IsDownloading = false;
          }
        });
      }
    });
  }

  handleShiftClick(event: MouseEvent, emp: Employee, date: string, shift: Shift) {
    event.stopPropagation();

    if (this.isViewerMode()) {
      return;
    }

    // Block click entirely for shifts from other shops
    const isOtherShop = !this.selectedShopIds.map(Number).includes(Number(shift.shop_id || shift.roster_shop_id || 0));
    if (isOtherShop) {
      return;
    }

    if (shift.is_readonly && !shift.is_finalized && this.header?.is_submitted != 1) {
      return;
    }
    this.openShiftModal(emp, date, shift);
  }

  handleEmployeeCellClick(emp: Employee) {
    const empName = this.getEmployeeDisplayName(emp);
    const warnings: string[] = [];

    const hasConsecutive = this.hasConsecutiveWorkDays(emp.id, 6);
    if (hasConsecutive) {
      warnings.push(`Assigned for 6 consecutive work days in this week.`);
    }

    const compliance = this.getComplianceStatus(emp);
    if (compliance.status !== 'ok') {
      warnings.push(compliance.message);
    }

    if (warnings.length > 0) {
      let title = '';
      let messageHtml = '';

      if (warnings.length === 1) {
        if (hasConsecutive) {
          title = 'Consecutive Days Warning';
          messageHtml = `Warning: <strong>${empName}</strong> has been assigned for 6 consecutive work days in this week.`;
        } else {
          title = 'Rostered Hours Warning';
          messageHtml = `Warning: <strong>${empName}</strong> - ${compliance.message}.`;
        }
      } else {
        title = 'Multiple Roster Warnings';
        messageHtml = `<div style="font-weight: 500; margin-bottom: 12px;">Warning: <strong>${empName}</strong> has multiple roster compliance issues:</div><ul style="margin: 0; padding-left: 20px; line-height: 1.6;">${warnings.map(w => `<li style="margin-bottom: 6px;">${w}</li>`).join('')}</ul>`;
      }

      const dialogData = new ConfirmDialogModel(title, messageHtml);
      dialogData.showNoButton = false;
      dialogData.yesButtonCaption = 'OK';
      this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: dialogData
      });
    }
  }

  isPastWeek(): boolean {
    const currentWeekStart = moment().startOf('isoWeek');
    return this.currentDate.isBefore(currentWeekStart, 'day');
  }

  isPastOrCurrentWeek(): boolean {
    const currentWeekStart = moment().startOf('isoWeek');
    return this.currentDate.isSameOrBefore(currentWeekStart, 'day');
  }

  isPastOrCurrentDate(dateStr: string): boolean {
    return moment(dateStr, 'YYYY-MM-DD').isSameOrBefore(moment(), 'day');
  }

  getSelectedShopName(): string {
    if (!this.selectedShopIds || this.selectedShopIds.length === 0) {
      return 'Select Shop';
    }
    const selectedId = this.selectedShopIds[this.selectedShopIds.length - 1];
    const shop = this.userShops.find(s => Number(s.id) === Number(selectedId));
    if (shop) return shop.name;

    if (this.shifts && this.shifts.length > 0) {
      const shiftWithShop = this.shifts.find(s => Number(s.shop_id || s.roster_shop_id) === Number(selectedId));
      if (shiftWithShop && shiftWithShop.shop_code) {
        return shiftWithShop.shop_code;
      }
    }
    return `Shop ${selectedId}`;
  }

  isShiftReviewed(shift: any): boolean {
    return !!(
      (shift.entry_reason && (Number(shift.entry_reason) > 0 || shift.entry_reason === 'other')) ||
      (shift.entry_reason_others && shift.entry_reason_others.trim() !== '')
    );
  }

  isMissingAttendance(shift: any): boolean {
    if (shift.is_readonly && !shift.is_finalized) return false;
    if (this.isShiftReviewed(shift)) return false;
    return this.getShiftStatus(shift) === 'red';
  }

  getShiftStatus(shift: any): 'green' | 'orange' | 'red' | 'purple' | 'default' {
    if (shift.is_finalized) {
      return 'default';
    }

    if (!this.isPastOrCurrentDate(shift.shift_date)) {
      return 'default';
    }

    if (shift.is_readonly) {
      return 'default';
    }

    if (shift.is_absent) {
      return 'default';
    }

    if (shift.is_unscheduled) {
      return 'purple';
    }

    const hasAttendance = !!(
      shift.attendance_id ||
      (shift.act_shift_start_time && shift.act_shift_start_time !== '' && shift.act_shift_start_time !== '00:00' && shift.act_shift_start_time !== '00:00:00') ||
      (shift.act_shift_end_time && shift.act_shift_end_time !== '' && shift.act_shift_end_time !== '00:00' && shift.act_shift_end_time !== '00:00:00')
    );

    if (!hasAttendance) {
      return 'red';
    }

    if (!shift.act_shift_start_time || shift.act_shift_start_time === '' || shift.act_shift_start_time === '00:00' || shift.act_shift_start_time === '00:00:00' ||
      !shift.act_shift_end_time || shift.act_shift_end_time === '' || shift.act_shift_end_time === '00:00' || shift.act_shift_end_time === '00:00:00') {
      return 'red';
    }

    // Retrieve thresholds (default: 7)
    const inBefore = Number(this.settingsColors?.att_threshold_in_before ?? 7);
    const inAfter = Number(this.settingsColors?.att_threshold_in_after ?? 7);
    const outBefore = Number(this.settingsColors?.att_threshold_out_before ?? 7);
    const outAfter = Number(this.settingsColors?.att_threshold_out_after ?? 7);

    const getMinutes = (timeStr: string) => {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length < 2) return null;
      return Number(parts[0]) * 60 + Number(parts[1]);
    };

    const isClockInOutside = (act: string, rost: string) => {
      const actMins = getMinutes(act);
      const rostMins = getMinutes(rost);
      if (actMins === null || rostMins === null) return false;
      let diff = actMins - rostMins;
      if (diff < -720) diff += 1440;
      if (diff > 720) diff -= 1440;
      return diff < -inBefore || diff > inAfter;
    };

    const isClockOutOutside = (act: string, rost: string) => {
      const actMins = getMinutes(act);
      const rostMins = getMinutes(rost);
      if (actMins === null || rostMins === null) return false;
      let diff = actMins - rostMins;
      if (diff < -720) diff += 1440;
      if (diff > 720) diff -= 1440;
      return diff < -outBefore || diff > outAfter;
    };

    if (isClockInOutside(shift.act_shift_start_time, shift.start_time) ||
      isClockOutOutside(shift.act_shift_end_time, shift.end_time)) {
      return 'orange';
    }

    if (shift.pos_id !== undefined && shift.pos_id !== null && Number(shift.pos_id) === 0) {
      return 'orange';
    }

    if (shift.entry_reason && Number(shift.entry_reason) > 0) {
      return 'orange';
    }

    if (!shift.start_time || !shift.end_time) {
      return 'orange';
    }

    return 'green';
  }

  getShiftWarningMessage(shift: any): string {
    const status = this.getShiftStatus(shift);
    if (status === 'red') {
      if (!shift.act_shift_start_time || shift.act_shift_start_time === '' || shift.act_shift_start_time === '00:00' || shift.act_shift_start_time === '00:00:00') {
        if (!shift.act_shift_end_time || shift.act_shift_end_time === '' || shift.act_shift_end_time === '00:00' || shift.act_shift_end_time === '00:00:00') {
          return 'Missing attendance';
        }
        return 'Clock in is missing';
      }
      return 'Clock out is missing';
    }
    if (status === 'purple') {
      return 'Unscheduled shift';
    }
    if (status === 'orange') {
      if (shift.pos_id !== undefined && shift.pos_id !== null && Number(shift.pos_id) === 0) {
        return 'Attendance is manually overridden';
      }
      if (this.isShiftReviewed(shift)) {
        return 'Attendance is manually overridden';
      }
      return 'Actual time deviates from Rostered by more than threshold';
    }
    return '';
  }

  getRoundedTime(timeStr: string): string {
    if (!timeStr || timeStr === '') return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let h = parseInt(parts[0], 10);
    const mVal = parseInt(parts[1], 10);
    let m = '00';
    if (mVal >= 8 && mVal < 23) {
      m = '15';
    } else if (mVal >= 23 && mVal < 38) {
      m = '30';
    } else if (mVal >= 38 && mVal < 53) {
      m = '45';
    } else if (mVal >= 53) {
      m = '00';
      h = (h + 1) % 24;
    }
    return `${h.toString().padStart(2, '0')}:${m}`;
  }

  getActualStartTimeDisplay(shift: any): string {
    const rawVal = shift.act_shift_start_time;
    if (!rawVal || rawVal === '' || rawVal === '00:00' || rawVal === '00:00:00') {
      return '--:--';
    }
    const val = this.showRounded ? this.getRoundedTime(rawVal) : rawVal;
    return val ? val.slice(0, 5) : '--:--';
  }

  getActualEndTimeDisplay(shift: any): string {
    const rawVal = shift.act_shift_end_time;
    if (!rawVal || rawVal === '' || rawVal === '00:00' || rawVal === '00:00:00') {
      return '--:--';
    }
    const val = this.showRounded ? this.getRoundedTime(rawVal) : rawVal;
    return val ? val.slice(0, 5) : '--:--';
  }

  getActualShiftDurationFormatted(shift: any): string {
    if (!shift.act_shift_start_time || shift.act_shift_start_time === '' || shift.act_shift_start_time === '00:00' || shift.act_shift_start_time === '00:00:00' ||
      !shift.act_shift_end_time || shift.act_shift_end_time === '' || shift.act_shift_end_time === '00:00' || shift.act_shift_end_time === '00:00:00') {
      return '--:--';
    }
    const actStart = this.showRounded ? this.getRoundedTime(shift.act_shift_start_time) : shift.act_shift_start_time;
    const actEnd = this.showRounded ? this.getRoundedTime(shift.act_shift_end_time) : shift.act_shift_end_time;
    const start = moment(actStart, 'HH:mm');
    const end = moment(actEnd, 'HH:mm');
    let durationMinutes = end.diff(start, 'minutes');
    if (durationMinutes < 0) durationMinutes += 24 * 60;

    if (shift.upb) {
      const upbParts = shift.upb.split(':');
      if (upbParts.length >= 2) {
        const upbMinutes = parseInt(upbParts[0], 10) * 60 + parseInt(upbParts[1], 10);
        durationMinutes = Math.max(0, durationMinutes - upbMinutes);
      }
    }

    if (durationMinutes === 0) {
      return '--:--';
    }

    const hours = durationMinutes / 60;
    return hours.toFixed(2) + 'h';
  }

  getThemeColor(emp: Employee): string {
    // If grade is <= 4, take bg_color from position, otherwise from employee type
    if (emp.grade <= 4) {
      return emp.position_bg_color || '#ebf8ff';
    }
    return emp.type_bg_color || '#f0fff4';
  }

  getThemeTextColor(emp: Employee): string {
    // Usually position colors might need specific text contrast
    if (emp.grade <= 4) {
      return emp.fg_color || '#2d3748';
    }
    return '#2d3748';
  }

  getContrastColor(hexColor: string): string {
    if (!hexColor) return '#2d3748';
    const hex = hexColor.replace('#', '');
    if (hex.length !== 6) return '#2d3748';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#2d3748' : '#ffffff';
  }

  isDirty(): boolean {
    return this.hasUnsavedChanges;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isDirty()) {
      $event.returnValue = true;
    }
  }

  isSendingMail = false;

  sendRosterMail() {
    if (!this.header || !this.header.id) {
      this.snackBarService.open('Roster header is not loaded.', 'error');
      return;
    }

    const clientBaseUrl = window.location.origin;
    const viewerLink = `${clientBaseUrl}/roster/viewer?shop_id=${this.selectedShopIds[0]}&week_number=${this.currentWeekNo}&year=${this.currentYear}`;

    const payload = {
      hdr_id: this.header.id,
      week_number: this.currentWeekNo,
      year: this.currentYear,
      shop_id: this.selectedShopIds[0],
      viewer_link: viewerLink
    };

    this.isSendingMail = true;
    this.snackBarService.open('Sending roster...', 'info');

    this.rosterService.sendRosterEmail(payload).subscribe({
      next: (res: any) => {
        this.isSendingMail = false;
        if (res && res.status === 'SUCCESS') {
          this.snackBarService.open(res.message || 'Roster emails sent successfully.', 'success');
        } else {
          this.snackBarService.open(res.message || 'Failed to send roster emails.', 'error');
        }
      },
      error: (err: any) => {
        this.isSendingMail = false;
        this.snackBarService.open(err?.error?.message || 'Failed to send roster emails.', 'error');
      }
    });
  }

  isPastDate(dateStr: string): boolean {
    if (!dateStr) return false;
    return moment(dateStr, 'YYYY-MM-DD').isBefore(moment(), 'day');
  }

  dropPredicate = (drag: import('@angular/cdk/drag-drop').CdkDrag<any>, drop: import('@angular/cdk/drag-drop').CdkDropList<any>) => {
    if (!this.canEditShift) {
      return false;
    }
    const data = drop.data;
    if (data && data.date) {
      return !this.isPastDate(data.date);
    }
    return true;
  };
}
